import { useMemo } from "react";

function Syringe({ volume, maxVolume, label }) {
  const pct = Math.min((volume / maxVolume) * 100, 100);

  // Build graduation marks
  const majorStep = maxVolume <= 1 ? 0.2 : maxVolume <= 3 ? 0.5 : 1;
  const marks = [];
  for (let v = majorStep; v <= maxVolume; v = Math.round((v + majorStep) * 100) / 100) {
    marks.push({ pct: (v / maxVolume) * 100, label: v % 1 === 0 ? String(v) : v.toFixed(1) });
  }

  return (
    <div className="w-full max-w-md mx-auto" data-testid={`syringe-${maxVolume}ml`}>
      <div className="text-[11px] text-muted-foreground font-semibold text-center mb-2">{label}</div>
      <div className="flex items-center gap-0">
        {/* Needle */}
        <div className="flex items-center shrink-0">
          <div style={{ width: 30, height: 3, background: '#888', borderRadius: 2 }} />
          <div style={{ width: 14, height: 14, background: '#777', borderRadius: 3 }} />
        </div>

        {/* Barrel */}
        <div className="relative flex-1" style={{ height: 44 }}>
          {/* Barrel background - white */}
          <div
            className="absolute inset-0 rounded"
            style={{ background: 'white', border: '2px solid #999' }}
          />

          {/* Blue liquid fill */}
          <div
            className="absolute top-[3px] bottom-[3px] left-[3px] rounded-sm"
            style={{
              width: `calc(${pct}% - 6px)`,
              background: 'linear-gradient(180deg, #60a5fa 0%, #2563eb 100%)',
              minWidth: pct > 0 ? 2 : 0,
              transition: 'width 0.3s ease',
            }}
            data-testid="syringe-fill"
          />

          {/* Red indicator line at fill level */}
          {pct > 0 && pct <= 100 && (
            <div
              className="absolute top-0 bottom-0 flex flex-col items-center"
              style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
            >
              {/* Arrow pointer */}
              <div style={{
                width: 0, height: 0,
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderTop: '8px solid #ef4444',
                marginTop: -8,
              }} />
              {/* Red line */}
              <div style={{
                width: 3,
                flex: 1,
                background: '#ef4444',
              }} />
              {/* Bottom arrow */}
              <div style={{
                width: 0, height: 0,
                borderLeft: '7px solid transparent',
                borderRight: '7px solid transparent',
                borderBottom: '8px solid #ef4444',
                marginBottom: -8,
              }} />
            </div>
          )}

          {/* Volume label above red line */}
          {pct > 0 && (
            <div
              className="absolute font-mono font-bold text-xs"
              style={{
                left: `${pct}%`,
                transform: 'translateX(-50%)',
                top: -22,
                color: '#ef4444',
                whiteSpace: 'nowrap',
              }}
              data-testid="syringe-volume-label"
            >
              {volume.toFixed(2)} mL
            </div>
          )}

          {/* Graduation marks */}
          {marks.map(m => (
            <div key={m.pct} className="absolute" style={{ left: `${m.pct}%`, top: 0, bottom: 0 }}>
              {/* Top mark */}
              <div style={{ position: 'absolute', top: 2, left: -0.5, width: 1, height: 12, background: '#555' }} />
              {/* Bottom mark */}
              <div style={{ position: 'absolute', bottom: 2, left: -0.5, width: 1, height: 12, background: '#555' }} />
              {/* Label */}
              <div
                className="absolute text-center font-mono"
                style={{ top: '100%', marginTop: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: '#999', fontWeight: 700 }}
              >
                {m.label}
              </div>
            </div>
          ))}

          {/* mL label at end */}
          <div
            className="absolute font-mono"
            style={{ right: -18, top: '100%', marginTop: 4, fontSize: 9, color: '#999' }}
          >
            mL
          </div>
        </div>

        {/* Plunger */}
        <div className="flex items-center shrink-0">
          <div style={{ width: 10, height: 30, background: '#999', borderRadius: 2 }} />
          <div style={{ width: 35, height: 4, background: '#999', borderRadius: 2 }} />
          <div style={{ width: 4, height: 40, background: '#999', borderRadius: 2 }} />
        </div>
      </div>
    </div>
  );
}

export default function SyringeVisual({ volume }) {
  const syringes = useMemo(() => {
    if (!volume || volume <= 0) return [];
    const sizes = [1, 3, 5, 10];
    const smallestIdx = sizes.findIndex(s => volume <= s);
    if (smallestIdx === -1) return [{ max: 10, label: "10 mL Syringe" }];
    const result = [{ max: sizes[smallestIdx], label: `${sizes[smallestIdx]} mL Syringe` }];
    // Add next largest size if available
    if (smallestIdx + 1 < sizes.length) {
      result.push({ max: sizes[smallestIdx + 1], label: `${sizes[smallestIdx + 1]} mL Syringe` });
    }
    return result;
  }, [volume]);

  if (!volume || volume <= 0) return null;

  return (
    <div className="space-y-6 mt-6 pt-4 border-t border-border/40" data-testid="syringe-visual">
      <p className="text-sm text-center">
        Draw <span className="font-mono font-bold text-primary text-base">{volume.toFixed(2)} mL</span> — fill to the <span className="text-red-500 font-bold">red</span> mark
      </p>
      {syringes.map(s => (
        <Syringe key={s.max} volume={volume} maxVolume={s.max} label={s.label} />
      ))}
    </div>
  );
}
